import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { PageTitle } from '@/admin/ui/Page';
import type { NormalizedQuestion } from '@/admin/ui/QuestionAnswerView';
import { QuestionAnswerView } from '@/admin/ui/QuestionAnswerView';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type QuizResponseDetail = components['schemas']['QuizResponseDetail'];
type QuestionWithSubmission = components['schemas']['QuestionWithSubmission'];

export const Route = createFileRoute('/admin/quiz-submissions/$submissionId')({
  component: QuizSubmissionDetail,
});

function statusBadge(detail: QuizResponseDetail): {
  label: string;
  variant: BadgeVariant;
} {
  if (!detail.complete)
    return {
      label: i18n.t('admin.quizSubmissions.status.inProgress'),
      variant: 'neutral',
    };
  if (detail.passed)
    return {
      label: i18n.t('admin.quizSubmissions.status.passed'),
      variant: 'success',
    };
  return {
    label: i18n.t('admin.quizSubmissions.status.failed'),
    variant: 'error',
  };
}

function toNormalized(q: QuestionWithSubmission): NormalizedQuestion {
  return {
    question_id: q.question_id,
    type: q.type,
    status: q.status,
    text: q.text,
    choices: q.choices,
    user_answer: q.user_answer,
    has_answer: q.has_answer,
    is_correct: q.is_correct,
    feedback: null,
  };
}

function QuizSubmissionDetail() {
  const { t } = useTranslation();
  const { submissionId } = Route.useParams();
  const { data, isLoading, error } = api.getQuizResponseDetail.useQuery({
    path: { response_id: Number(submissionId) },
  });

  if (isLoading) {
    return (
      <LoadingMessage>{t('admin.quizSubmissions.loadingOne')}</LoadingMessage>
    );
  }
  if (error) {
    return (
      <ErrorMessage>{t('admin.quizSubmissions.errorLoadingOne')}</ErrorMessage>
    );
  }
  if (!data) {
    return <ErrorMessage>{t('admin.quizSubmissions.notFound')}</ErrorMessage>;
  }

  const { label, variant } = statusBadge(data);

  return (
    <Container>
      <BackLink to="/admin/quiz-submissions/">
        {t('admin.quizSubmissions.backToList')}
      </BackLink>

      <Header>
        <PageTitle>
          {t('admin.quizSubmissions.title', {
            id: data.id,
            interpolation: { escapeValue: false },
          })}
        </PageTitle>
        <HeaderActions>
          <Link
            to="/admin/feedbacks/"
            search={{ quiz_response_id: data.id }}
            style={{
              color: 'var(--blue)',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('admin.quizSubmissions.viewFeedback')}
          </Link>
          <Badge $variant={variant} $size="md">
            {label}
          </Badge>
        </HeaderActions>
      </Header>

      <Section>
        <SectionTitle>{t('admin.common.overview')}</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.student')}</FieldLabel>
            <FieldValue>
              {data.user_display_name}
              <br />
              <small style={{ color: 'var(--grey-600)' }}>
                {data.user_email}
              </small>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.course')}</FieldLabel>
            <FieldValue>{data.course_name}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.topics.lesson')}</FieldLabel>
            <FieldValue>{data.lesson_name}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.score')}</FieldLabel>
            <FieldValue>{data.score}%</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.submitted')}</FieldLabel>
            <FieldValue>
              {new Date(data.created_at).toLocaleString()}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>
          {t('admin.quizSubmissions.questionsAndAnswers')}
        </SectionTitle>
        {data.questions.length === 0 ? (
          <EmptyMessage>{t('admin.quizSubmissions.noQuestions')}</EmptyMessage>
        ) : (
          data.questions.map((q, idx) => (
            <QuestionAnswerView
              key={q.question_id}
              question={toNormalized(q)}
              index={idx + 1}
            />
          ))
        )}
      </Section>
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  align-items: center;
  color: var(--blue);
  display: inline-flex;
  font-size: 0.875rem;
  gap: 0.5rem;
  margin-bottom: 1rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
`;

const EmptyMessage = styled.div`
  color: var(--grey-600);
  padding: 1rem;
`;

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
